import { useTranslation } from 'react-i18next';

import { Flex, Text } from '@bonusly/design-system';

import type { BizyFollowUpsNode } from '../../../../lib/bizy/types/nodes';
import { useBizyChatActions } from '../context/actions-context';
import { BizyAnalyticsEvent, useBizyAnalytics } from '../context/analytics-context';
import { ArrowUpOutline } from '../lib/icons';
import type { BizyNodeProps } from './node-props';

/**
 * Renders the Data Agent's follow-up suggestions as one-tap rows under a
 * "Follow-ups" heading. Clicking sends the suggestion verbatim. Suppressed in
 * read-only views (no sending on the viewer's behalf).
 */
export function FollowUpsNode({ node }: BizyNodeProps) {
	const { suggestions } = (node as BizyFollowUpsNode).payload;
	const { t } = useTranslation('admin_bizy');
	const { track } = useBizyAnalytics();
	const { fontSize = 'sm', isReadOnly, followUpsDisabled, onSelectFollowUp } = useBizyChatActions();

	if (isReadOnly || !onSelectFollowUp) return null;

	const items = (suggestions ?? []).filter((suggestion) => suggestion.trim().length > 0);
	if (items.length === 0) return null;

	const handleSelect = (suggestion: string, position: number) => {
		if (followUpsDisabled) return;
		track(BizyAnalyticsEvent.FollowUpClicked, { position, path: window.location.pathname });
		onSelectFollowUp(suggestion);
	};

	return (
		<Flex direction="column" gap={2} mb={4} data-testid="bizy-follow-ups">
			<Text fontSize="md" fontWeight="semibold" color="text.default" mb={0.5}>
				{t('followUps.heading', 'Follow-ups')}
			</Text>
			{items.map((suggestion, idx) => (
				<Flex
					key={idx}
					as="button"
					type="button"
					disabled={followUpsDisabled}
					aria-label={suggestion}
					onClick={() => handleSelect(suggestion, idx)}
					align="center"
					gap={3}
					textAlign="left"
					borderWidth="1px"
					borderColor="border.soft"
					borderRadius={12}
					px={3}
					py={2.5}
					opacity={followUpsDisabled ? 0.5 : 1}
					cursor={followUpsDisabled ? 'not-allowed' : 'pointer'}
					transition="background 0.15s ease, border-color 0.15s ease"
					{...(!followUpsDisabled && { _hover: { bg: 'secondary.bgAlpha', borderColor: 'border.default' } })}
				>
					<Flex align="center" justify="center" minW={7} h={7} flexShrink={0} borderRadius="full" bg="secondary.bg" color="secondary.fgOnSurface">
						<ArrowUpOutline boxSize={4} />
					</Flex>
					<Text fontSize={fontSize} color="text.default" lineHeight="short">
						{suggestion}
					</Text>
				</Flex>
			))}
		</Flex>
	);
}
